import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getLegacyPatients } from '../../util/patientsApi';
import { getLegacyTrials } from '../../util/reportAPI';
import { formatRecordingDateTime } from '../../util/dateFormat';
import { isMeaningfulTagValue } from '../../util/trialTagFields';
import {
  LEGACY_ROOT_PATH,
  editableLegacyTags,
  formatLegacyDate,
  groupTrialsBySession,
  hasBadTriangulation,
  legacyDashboardPath,
  legacyReviewPath,
  sessionKeyOf,
} from '../../util/legacyAnalytics';
import TrialTagChips from '../record/TrialTagChips';
import Spinner from '../decorations/Spinner';
import LegacyTriangulationBadge from './LegacyTriangulationBadge';
import '../../../dist/assets/stylesheets/css/patients.css';
import '../../../dist/assets/stylesheets/css/legacyAnalytics.css';

/** Research tag columns from the old lab database, in table order. */
const TAG_COLUMNS = [
  { key: 'action', label: 'Action' },
  { key: 'walkingform', label: 'Walking form' },
  { key: 'cond', label: 'Condition' },
  { key: 'aidtype', label: 'Aid' },
  { key: 'speed', label: 'Speed', read: speedCell },
];

const SEARCH_KEYS = [
  'id', 'name', 'action', 'walkingform', 'cond', 'aidtype', 'speed', 'treadspeed',
  'lab', 'device', 'stimloc', 'stimtype', 'stimpos', 'comments', 'session',
];

function displayValue(value) {
  return isMeaningfulTagValue(value) ? String(value) : '—';
}

/**
 * Legacy rows mostly carry treadmill speed in `treadspeed` and leave `speed`
 * empty. Show whichever is recorded; both when both are.
 */
function speedCell(trial) {
  const parts = [
    { value: trial.speed, label: 'Speed' },
    { value: trial.treadspeed, label: 'Treadmill speed' },
  ].filter((part) => isMeaningfulTagValue(part.value));
  if (!parts.length) return { text: '—' };
  return {
    text: parts.map((part) => String(part.value)).join(' · '),
    title: parts.map((part) => `${part.label}: ${part.value}`).join(', '),
  };
}

function readColumn(col, trial) {
  return col.read ? col.read(trial) : { text: displayValue(trial[col.key]) };
}

function tagChips(trial) {
  return editableLegacyTags(trial.tags).map((tag, index) => ({
    key: `tag-${index}`,
    label: 'Tag',
    value: tag,
  }));
}

function LegacyPatientDashboard() {
  const { patientID } = useParams();
  const [patient, setPatient] = useState(null);
  const [trials, setTrials] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    setTrials(null);
    Promise.all([getLegacyPatients(patientID), getLegacyTrials(patientID)]).then(([list, res]) => {
      if (cancelled) return;
      setPatient((Array.isArray(list) && list[0]) || null);
      setTrials((res && res.result) || []);
    });
    return () => {
      cancelled = true;
    };
  }, [patientID]);

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = (trials || []).filter((trial) =>
      !needle ||
      SEARCH_KEYS.some((key) => String(trial[key] ?? '').toLowerCase().includes(needle)) ||
      editableLegacyTags(trial.tags).some((tag) => tag.toLowerCase().includes(needle))
    );
    return groupTrialsBySession(matches);
  }, [trials, query]);

  if (trials === null) return <Spinner />;

  const sessionCount = new Set(trials.map(sessionKeyOf).filter(Boolean)).size;
  const columnCount = 3 + TAG_COLUMNS.length;
  const patientName = patient?.patient_name || `Patient ${patientID}`;

  return (
    <div className="parent-container legacy-page">
      <nav className="trial-breadcrumb legacy-breadcrumb">
        <Link to={LEGACY_ROOT_PATH} className="trial-breadcrumb-link">
          Legacy analytics
        </Link>
        <span className="legacy-breadcrumb__sep">{'>'}</span>
        <span className="legacy-breadcrumb__current">{patientName}</span>
      </nav>
      <p className="legacy-page__totals">
        {trials.length} trials · {sessionCount} sessions
      </p>
      {trials.length === 0 ? (
        <p className="legacy-empty">No legacy trials for this patient.</p>
      ) : (
        <section className="patient-table container legacy-trials">
          <table>
            <tbody>
              <tr>
                <td colSpan={columnCount} style={{ height: 'fit-content' }}>
                  <input
                    id="search-bar"
                    placeholder="Search trials, tags, comments..."
                    aria-label="Search legacy trials"
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <th>Trial ID</th>
                <th>Recorded</th>
                {TAG_COLUMNS.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th>Tags</th>
              </tr>
              {groups.map((group) => (
                <React.Fragment key={group.sessionKey || 'no-session'}>
                  <tr className="legacy-session-row">
                    <th colSpan={columnCount} scope="rowgroup">
                      <span className="legacy-session-row__name">
                        {group.sessionKey || 'No session'}
                      </span>
                      <span className="legacy-session-row__meta">
                        {formatLegacyDate(group.startMs, group.trials[0].recorded_timezone)}
                        {' · '}
                        {group.trials.length} {group.trials.length === 1 ? 'trial' : 'trials'}
                      </span>
                    </th>
                  </tr>
                  {group.trials.map((trial) => {
                    const url = {
                      pathname: legacyReviewPath(patientID, trial.id),
                      state: { backUrl: legacyDashboardPath(patientID), backName: 'Dashboard' },
                    };
                    return (
                      <tr className="patient-row" key={trial.id}>
                        <td><Link to={url}><div title={trial.name}>{trial.id}</div></Link></td>
                        <td>
                          <Link to={url}>
                            <div>
                              {formatRecordingDateTime(trial.date, trial.recorded_timezone, { withSeconds: false })}
                            </div>
                          </Link>
                        </td>
                        {TAG_COLUMNS.map((col) => {
                          const cell = readColumn(col, trial);
                          return (
                            <td key={col.key}><Link to={url}><div title={cell.title}>{cell.text}</div></Link></td>
                          );
                        })}
                        <td>
                          <Link to={url}>
                            <div className="legacy-trial-tags">
                              {hasBadTriangulation(trial) ? <LegacyTriangulationBadge compact /> : null}
                              <TrialTagChips items={tagChips(trial)} />
                            </div>
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
              {groups.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="legacy-empty">No trials match this search.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

export default LegacyPatientDashboard;
